import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { isKnownIssueActive, isUnsupported, knownIssuesApi, localizedKnownIssue } from '../lib/api';
import { t, useLocale } from '../lib/i18n';
import type { KnownIssue } from '../lib/types';

/**
 * The operator's declared incident, above the agent's queue.
 *
 * The bot already gets these notices injected into its guidance; agents were
 * the half that was missing, which is how two people diagnose the same outage
 * from scratch while support has known about it since morning.
 *
 * Fails silent by design: a 404/503 on the notices endpoint renders nothing.
 * The inbox is the agent's job — it must not break because an incident-banner
 * lookup did.
 */

/** How often we re-ask the server. Matches the backend's 60s cache TTL, which
 *  is the staleness bound this feature is designed around. */
const POLL_MS = 60_000;
/** How often the window (startsAt/endsAt) is re-evaluated against the clock. */
const TICK_MS = 30_000;
/** More than this and the banner is a status page. The rest are counted. */
const MAX_SHOWN = 2;

/**
 * Notices this agent hid, for THIS page load only.
 *
 * Deliberately in memory and not local/sessionStorage: an incident an agent
 * dismissed at 09:00 must be back in front of them after a reload while it is
 * still open, and storage would have kept it hidden for the whole outage. The
 * module scope (rather than component state) means walking to Settings and back
 * does not re-nag them either.
 */
const dismissed = new Set<string>();

export function KnownIssueBanner() {
  const { claims } = useAuth();
  const tenantCode = claims?.tenantCode ?? '';
  const isAdmin = claims?.role === 'tenant_admin' || claims?.role === 'super_admin';
  const { locale } = useLocale();
  const [issues, setIssues] = useState<KnownIssue[]>([]);
  const [now, setNow] = useState(() => Date.now());
  // Dismissals live in a module Set (see above); this counter only forces the
  // re-render, since mutating a Set is invisible to React.
  const [hiddenTick, setHiddenTick] = useState(0);

  useEffect(() => {
    if (!tenantCode) return;
    let cancelled = false;
    let poll: ReturnType<typeof setInterval> | null = null;
    const stopPolling = () => {
      if (poll) clearInterval(poll);
      poll = null;
    };
    const fetchOnce = async () => {
      try {
        const r = await knownIssuesApi.list(tenantCode);
        if (cancelled) return;
        if (isUnsupported(r)) {
          // A backend without the route will never answer differently, so stop
          // asking once a minute for the rest of the shift.
          setIssues([]);
          stopPolling();
          return;
        }
        setIssues(r.issues ?? []);
        setNow(Date.now());
      } catch (err) {
        // Never surfaced to the agent: a broken notice lookup is not their
        // problem, and an error strip above the queue would be permanent noise.
        console.warn('[known-issues] banner lookup failed', err);
      }
    };
    void fetchOnce();
    poll = setInterval(() => void fetchOnce(), POLL_MS);
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [tenantCode]);

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(tick);
  }, []);

  // `isActive` off the wire is only the server's opinion at fetch time; the
  // window is re-checked here so a notice ends on time rather than on reload.
  const active = useMemo(() => issues.filter((i) => isKnownIssueActive(i, now)), [issues, now]);
  const visible = useMemo(
    () => active.filter((i) => !dismissed.has(i.id)),
    // hiddenTick is the re-render trigger for the module-level Set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [active, hiddenTick],
  );

  if (visible.length === 0) return null;
  const shown = visible.slice(0, MAX_SHOWN);
  const extra = visible.length - shown.length;

  return (
    <div className="shrink-0 border-b border-red-200 bg-red-50 px-3 py-2 text-red-900">
      {shown.map((issue) => {
        const { title, message } = localizedKnownIssue(issue, locale);
        return (
          <div key={issue.id} className="flex items-start gap-2 py-0.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            <div className="min-w-0 flex-1" dir="auto">
              <div className="text-xs font-semibold">
                <span className="me-1.5 rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-medium text-white">
                  {t('inbox.knownIssue.label')}
                </span>
                {title}
              </div>
              {message && <div className="text-xs leading-snug">{message}</div>}
              <div className="mt-0.5 text-[11px] text-red-700/80">{t('inbox.knownIssue.botTelling')}</div>
            </div>
            {isAdmin && (
              <Link
                to="/settings/known-issues"
                className="shrink-0 text-[11px] underline decoration-red-300 hover:decoration-red-600"
              >
                {t('inbox.knownIssue.manage')}
              </Link>
            )}
            <button
              type="button"
              onClick={() => {
                dismissed.add(issue.id);
                setHiddenTick((n) => n + 1);
              }}
              title={t('inbox.knownIssue.dismissTitle')}
              aria-label={t('inbox.knownIssue.dismiss')}
              className="shrink-0 rounded p-0.5 text-red-500 hover:bg-red-100 hover:text-red-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
      {extra > 0 && (
        <div className="ps-6 text-[11px] text-red-700/80">{t('inbox.knownIssue.more', { n: extra })}</div>
      )}
    </div>
  );
}
